import { useState, useEffect, useCallback } from 'react'
import type { ConversationState, ConversationActions } from '../hooks/useConversation'
import type { IncidentForm, IncidentCategory } from '../types/incident'
import { useOfflineDictation } from '../hooks/useOfflineDictation'
import { useOnline } from '../hooks/useNetwork'
import { pwaApi } from '../api'
import { getNote, updateNote } from '../engines/offline_storage'
import { formatIncidentTitle } from '../utils/incidentParser'
import { recordCorrection, recordCategoryCorrection } from '../engines/shared/dictionary_service'
import { setDictationActive } from '../pwaUpdate'
import { BottomActionBar } from '../components/BottomActionBar'
import { ArrangementList } from '../components/ArrangementList'
import type { Arrangement } from '../types/arrangement'
import { useSwipeBack } from '../hooks/useSwipeBack'

interface Props {
  positions: any
  conv: ConversationState & ConversationActions
  onGoToE2: () => void
  onGoToE1: () => void
}

// Pole "Ważność" celowo usunięte z UI — słowo określające pilność (np. "pilnie",
// "natychmiast") i tak trafia naturalnie do treści poleceń/opisu (np. "potrzebny
// serwis pilnie"), więc osobne pole tylko duplikowało tę samą informację.
const LABELS: Record<Exclude<keyof IncidentForm, 'polecenia_arr' | 'kategoria' | 'waznosc'>, string> = {
  lokalizacja: 'Lokalizacja',
  obiekt: 'Obiekt / maszyna',
  typ: 'Typ zdarzenia',
  opis: 'Opis zdarzenia',
  polecenia: 'Polecenia / zlecenia',
  ppe: 'PPE / środki ochrony',
}

const FIELDS: Exclude<keyof IncidentForm, 'polecenia_arr' | 'kategoria' | 'waznosc'>[] = ['lokalizacja', 'obiekt', 'typ', 'opis', 'polecenia', 'ppe']

const CATEGORIES: IncidentCategory[] = ['Awaria', 'BHP', 'Magazyn']

export function IncidentSummaryScreen({ positions, conv, onGoToE2, onGoToE1 }: Props) {
  const form = conv.incidentForm
  const isOnline = useOnline()
  const [liveText, setLiveText] = useState('')
  const [dictating, setDictating] = useState(false)
  const [localForm, setLocalForm] = useState<IncidentForm | null>(form)

  // Bezpiecznik auto-aktualizacji PWA: nie przeładuj strony w trakcie dyktowania.
  // Cleanup przy odmontowaniu jest konieczny — bez niego zejście z ekranu w trakcie
  // dyktowania zostawiało globalną flagę "true" na zawsze i PWA nigdy więcej się nie aktualizowało.
  useEffect(() => {
    setDictationActive(dictating)
    return () => setDictationActive(false)
  }, [dictating])
  const [poleceniaArr, setPoleceniaArr] = useState<Arrangement[]>(() => {
    const p = form?.polecenia
    return p ? p.split('\n').filter(Boolean).map((text, i) => ({
      id: `p-${Date.now()}-${i}`, text: text.trim(), done: false,
    })) : []
  })

  useEffect(() => {
    setLocalForm(prev => (prev === form ? prev : form))
  }, [form])
  useEffect(() => { if (localForm) conv.setIncidentForm(localForm) }, [localForm])

  const addDictation = useOfflineDictation({
    onSegment: (t) => { setLiveText(t) },
    onDone: (full) => {
      setDictating(false); setLiveText('')
      const lines = full.split('\n').filter(Boolean).map(text => text.trim()).filter(Boolean)
      const newItems: Arrangement[] = lines.map((text, i) => ({
        id: `p-${Date.now()}-${i}-${Math.random().toString(36).slice(2)}`,
        text, done: false,
      }))
      setPoleceniaArr(prev => [...prev, ...newItems])
    },
    onError: () => { setDictating(false) },
  })

  const startAdd = useCallback(() => {
    if (dictating) { addDictation.stop(); return }
    setDictating(true); setLiveText(''); addDictation.start()
  }, [dictating, addDictation])

  if (!localForm) return null

  const recordFieldCorrections = async (confirmedForm: IncidentForm) => {
    const original = conv.originalIncidentForm
    if (!original) return
    try {
      if (original.kategoria && confirmedForm.kategoria && original.kategoria !== confirmedForm.kategoria) {
        await recordCategoryCorrection(original.kategoria, confirmedForm.kategoria, 'galvo', 'galvo', undefined, original.opis)
      }
      if (original.obiekt !== confirmedForm.obiekt) {
        await recordCorrection('equipment', original.obiekt || '', confirmedForm.obiekt || '', 'galvo', 'galvo')
      }
      if (original.typ !== confirmedForm.typ) {
        await recordCorrection('failure', original.typ || '', confirmedForm.typ || '', 'galvo', 'galvo')
      }
    } catch (err) {
      console.warn('[IncidentSummary] recordFieldCorrections failed:', err)
    }
  }

  const handleConfirm = async () => {
    if (!localForm) return
    const createdAt = new Date().toISOString()
    const status = isOnline ? 'sent' : 'queued'
    const poleceniaText = poleceniaArr.map(a => a.text).join('\n')
    const confirmedForm = { ...localForm, polecenia: poleceniaText, polecenia_arr: poleceniaArr }

    await recordFieldCorrections(confirmedForm)

    if (!isOnline && conv.lastNoteId) {
      // OFFLINE: notatka już istnieje w IndexedDB (NoteRecord) — aktualizujemy ją
      // i NIC więcej. Wcześniej ten sam incydent dodatkowo trafiał do localStorage
      // 'incidentHistory' z innym id (inc-...) niż NoteRecord (note_...), więc
      // deduplikacja na liście E1 (po id) nigdy się nie udawała — incydent pojawiał
      // się dwa razy (raz pełny z IndexedDB, raz okrojony z incidentHistory).
      const note = await getNote(conv.lastNoteId)
      if (note) {
        const { bold, rest } = formatIncidentTitle(confirmedForm)
        note.incidentForm = confirmedForm
        note.title = `${bold}${rest}`
        note.shortNote = `${confirmedForm.lokalizacja || ''} | ${confirmedForm.obiekt || ''} | ${confirmedForm.typ || ''}`.replace(/^\s*\|\s*|\s*\|\s*$/g, '').replace(/^\|+\s*|\s*\|+$/g, '')
        note.completed = true
        await updateNote(note)
      }
    } else if (isOnline) {
      // ONLINE: nie ma lokalnego NoteRecord (ścieżka online nie korzysta z IndexedDB),
      // więc incidentHistory w localStorage jest JEDYNYM źródłem tego wpisu na liście E1.
      try { await pwaApi.sendIncidentReport(confirmedForm) } catch (err) { console.error('[IncidentSummary] send failed:', err) }

      const history = JSON.parse(localStorage.getItem('incidentHistory') || '[]') as any[]
      const now = new Date(createdAt).getTime()
      const duplicate = history.find((h: any) =>
        Math.abs(now - new Date(h.createdAt).getTime()) < 5000 &&
        h.lokalizacja === confirmedForm.lokalizacja &&
        h.obiekt === confirmedForm.obiekt &&
        h.typ === confirmedForm.typ &&
        h.waznosc === confirmedForm.waznosc &&
        h.opis === confirmedForm.opis
      )
      if (!duplicate) {
        history.unshift({ id: `inc-${Date.now()}`, ...confirmedForm, createdAt, status })
        localStorage.setItem('incidentHistory', JSON.stringify(history.slice(0, 50)))
      }
    }

    conv.loadConversationItems()
    onGoToE1()
  }

  // Swipe left = zatwierdzenie i powrót do E1 — tak jak przycisk potwierdzenia.
  const swipeBack = useSwipeBack(() => { handleConfirm() })

  return (
    <div className="relative w-full h-full bg-[#0a0a0f]" onTouchStart={swipeBack.onTouchStart} onTouchEnd={swipeBack.onTouchEnd}>
      <div style={{
        position: 'absolute', top: `${positions.titleTop}%`, left: 0, right: 0,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '0 24px', color: '#D9D9D9', fontFamily: '"Inter", sans-serif',
      }}>
        <button onClick={onGoToE2} style={{
          color: '#fff', fontSize: 15, background: 'transparent', border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: 8, padding: '4px 12px',
        }}>
          Wróć
        </button>
        <span style={{ fontSize: 'clamp(24px, 4vw, 32px)', fontWeight: 200, letterSpacing: 3 }}>Podsumowanie</span>
        <div style={{ width: 70 }} />
      </div>

      <div style={{
        position: 'absolute', top: `${positions.decisionTop}%`,
        left: '4.8%', right: '4.8%', height: `${positions.decisionHeightPct}%`,
        borderRadius: 33, overflow: 'hidden',
        border: '1px solid rgba(255,255,255,0.05)',
        boxShadow: 'inset 1px 1px 2px rgba(255,255,255,0.15)',
        backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
        background: 'linear-gradient(180deg, rgba(0, 255, 85, 0.08) 4.81%, rgba(0, 255, 85, 0.21) 56.33%, rgba(0, 255, 85, 0.08) 100%)',
      }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 28, background: 'linear-gradient(to bottom, rgba(0,0,0,0.30) 0%, transparent 100%)', pointerEvents: 'none', zIndex: 10 }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 10, background: 'linear-gradient(to top, rgba(0,0,0,0.12) 0%, transparent 100%)', pointerEvents: 'none', zIndex: 10 }} />
        <div className="no-scrollbar" style={{ padding: '28px 22px', height: '100%', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 10, marginBottom: 1 }}>Kategoria</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {CATEGORIES.map(cat => {
                const selected = localForm.kategoria === cat
                return (
                  <div
                    key={cat}
                    onClick={() => setLocalForm(prev => prev ? { ...prev, kategoria: cat } : prev)}
                    style={{
                      padding: '5px 12px',
                      borderRadius: 999,
                      fontSize: 12,
                      cursor: 'pointer',
                      border: `1px solid ${selected ? 'rgba(0,255,85,0.6)' : 'rgba(255,255,255,0.15)'}`,
                      background: selected ? 'rgba(0,255,85,0.18)' : 'rgba(0,0,0,0.20)',
                      color: selected ? '#86efac' : 'rgba(255,255,255,0.5)',
                      transition: 'all 150ms ease',
                    }}
                  >
                    {cat}
                  </div>
                )
              })}
            </div>
          </div>

          {FIELDS.map(field => (
            <div key={field} style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 10, marginBottom: 1 }}>
                {LABELS[field]}
              </div>
              {field === 'polecenia' ? (
                poleceniaArr.length === 0 ? <div style={{ color: 'rgba(255,255,255,0.25)', fontSize: 15 }}>—</div>
                  : <ArrangementList
                      items={poleceniaArr}
                      mode="e1"
                      onToggleDone={(id) => setPoleceniaArr(prev => prev.map(a => a.id === id ? { ...a, done: !a.done, doneAt: a.done ? undefined : new Date().toISOString() } : a))}
                      onChange={(id, fields) => setPoleceniaArr(prev => prev.map(a => a.id === id ? { ...a, ...fields } : a))}
                    />
              ) : (
                <div style={{ color: '#fff', fontSize: 15, lineHeight: 1.3, whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                  {localForm[field] || '—'}
                </div>
              )}
            </div>
          ))}

          {dictating && (
            <div style={{ color: '#0f0', fontSize: 13, marginTop: 8 }}>
              {liveText || 'Słucham - dodaję polecenie...'}
            </div>
          )}
        </div>
      </div>

      <div style={{
        position: 'absolute', bottom: `${positions.actionBarBottom}%`,
        left: 0, right: 0, display: 'flex', justifyContent: 'center',
        transformOrigin: 'center bottom',
      }}>
        <BottomActionBar
          onConfirmTap={handleConfirm}
          onMicTap={startAdd}
          isRecording={dictating}
        />
      </div>
    </div>
  )
}
